"use client";

/**
 * Bottom sheet that slides up inside the support panel when the user taps
 * "운영시간 보기" in the header. Confined to the panel area (no portal) so
 * the rest of the page UI stays untouched.
 */

import { useEffect } from "react";
import { useTranslations } from "next-intl";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";

interface BusinessHoursSheetProps {
  open: boolean;
  onClose: () => void;
}

export function BusinessHoursSheet({ open, onClose }: BusinessHoursSheetProps) {
  const t = useTranslations("support.businessHours");

  // Close on ESC for keyboard users.
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, onClose]);

  // Stay mounted so the closing transition can play; pointer-events flip
  // makes the sheet inert when it's animating out.
  return (
    <div
      className={cn(
        "absolute inset-0 z-30",
        open ? "pointer-events-auto" : "pointer-events-none",
      )}
      aria-hidden={!open}
    >
      {/* Full-area dim overlay sits behind the sheet so the rounded top
          corners of the sheet are framed by darkened panel area, not by the
          panel's white background. */}
      <button
        type="button"
        aria-label={t("close")}
        onClick={onClose}
        tabIndex={open ? 0 : -1}
        className={cn(
          "absolute inset-0 bg-black/40 transition-opacity duration-200 ease-out",
          open ? "opacity-100" : "opacity-0",
        )}
      />
      <div
        className={cn(
          // box-shadow follows the visual (post-transform) box, so a
          // translated-out sheet would still leak its top shadow into the
          // panel — toggle the shadow off when closed.
          // The cubic-bezier here matches iOS-style sheet curves: a quick
          // initial lift then a long, soft glide into rest. Pure `ease-out`
          // felt mechanical because it linearizes the start.
          "absolute inset-x-0 bottom-0 rounded-t-2xl bg-white pb-[max(1rem,var(--loc-safe-area-inset-bottom))] transition-[transform,box-shadow] duration-420 ease-[cubic-bezier(0.32,0.72,0,1)] will-change-transform",
          open
            ? "translate-y-0 shadow-[0_-12px_40px_-8px_rgba(0,0,0,0.25)]"
            : "translate-y-full shadow-none",
        )}
      >
        <div className="flex items-center justify-between px-4 pt-3.5 pb-2">
          <h3 className="text-[15px] font-semibold text-gray-900">
            {t("title")}
          </h3>
          <button
            type="button"
            onClick={onClose}
            aria-label={t("close")}
            tabIndex={open ? 0 : -1}
            className="-mr-1 flex h-8 w-8 items-center justify-center rounded-full text-gray-500 transition hover:bg-gray-100"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="px-4 pt-1 pb-3">
          <div className="flex items-start justify-between gap-3">
            <p className="flex items-center gap-2 text-[14px] font-medium text-gray-900">
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-500" />
              {t("weekdays")}
            </p>
            <p className="text-[14px] text-gray-700">{t("hours")}</p>
          </div>
          <p className="mt-2 text-[12px] text-gray-400">{t("timezone")}</p>
        </div>
      </div>
    </div>
  );
}
